'use client';

import { useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import ListItemText from '@mui/material/ListItemText';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';

import { useKontaktKandidater } from 'src/actions/kontakter';

import type { Kontakt, KontaktTyp } from 'src/actions/kontakter';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: () => void;
  onLäggTill: (typ: KontaktTyp, målId: string) => Promise<void>;
  befintliga: Kontakt[];
};

/**
 * Väljaren för att lägga till en kontakt för hand. Kandidaterna kommer från servern och är
 * redan filtrerade — ett hem vars ägare dolt sig visas som "Ett hem i föreningen", så sökrutan
 * kan inte användas för att avslöja vem som bor var.
 */
export function KontaktSökDialog({ open, onClose, onLäggTill, befintliga }: Props) {
  const [fråga, setFråga] = useState('');
  const { kandidater, kandidaterLoading } = useKontaktKandidater(open, fråga);

  /** Redan tillagda visas som tillagda i stället för att gå att lägga till två gånger. */
  const redanTillagda = useMemo(
    () => new Set(befintliga.map((k) => `${k.typ}:${k.målId}`)),
    [befintliga]
  );

  const läggTill = async (kandidat: Kontakt) => {
    await onLäggTill(kandidat.typ, kandidat.målId);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Lägg till kontakt</DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <TextField
          fullWidth
          autoFocus
          size="small"
          value={fråga}
          onChange={(event) => setFråga(event.target.value)}
          placeholder="Sök person, hem eller förening"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            },
          }}
        />

        <Scrollbar sx={{ mt: 2, height: 320 }}>
          {kandidater.length === 0 && !kandidaterLoading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Inget att lägga till här.
              </Typography>
            </Box>
          ) : (
            <MenuList>
              {kandidater.map((kandidat) => {
                const tillagd = redanTillagda.has(`${kandidat.typ}:${kandidat.målId}`);
                return (
                  <MenuItem
                    key={`${kandidat.typ}:${kandidat.målId}`}
                    disabled={tillagd}
                    onClick={() => läggTill(kandidat)}
                    sx={{ p: 1, gap: 1.5 }}
                  >
                    <Avatar
                      alt={kandidat.visningsnamn}
                      src={kandidat.avatarUrl ?? undefined}
                    >
                      <Iconify width={20} icon={kandidat.ikon} />
                    </Avatar>

                    <ListItemText
                      primary={kandidat.visningsnamn}
                      secondary={kandidat.underrubrik ?? kandidat.typEtikett}
                      slotProps={{
                        primary: { noWrap: true },
                        secondary: {
                          noWrap: true,
                          sx: { typography: 'caption', color: 'text.disabled' },
                        },
                      }}
                    />

                    {tillagd && <Chip size="small" variant="soft" label="Tillagd" />}
                  </MenuItem>
                );
              })}
            </MenuList>
          )}
        </Scrollbar>
      </DialogContent>
    </Dialog>
  );
}
